/**
 * Relevance scoring for recipe search.
 *
 * Replaces the old `Array.filter(text.includes(q))`, which had three problems:
 * accents broke equality, substrings crossed word boundaries (`ail` matched
 * *cocktail*), and every hit ranked identically regardless of whether the word
 * was in the title or buried in ingredient #14.
 *
 * The tiers below are a cost cascade as much as a relevance ranking: the exact
 * and prefix tiers are array lookups over pre-folded tokens, and edit distance
 * only runs when those cheap tiers come up short.
 */

import { SearchDoc } from './recipeSearchIndex';
import { PersonalRecipeIndexEntry } from './personalRecipeIndex';
import { tokenize, stemToken, foldText } from '../utils/searchNormalize';
import { levenshteinDistance } from '../utils/stringMatching';

export interface ScoredResult {
  recipe: PersonalRecipeIndexEntry;
  score: number;
  /** How many query words this recipe matched. */
  matchedWords: number;
  /** True when the recipe only matched via the fuzzy tier. */
  fuzzyOnly: boolean;
}

/** A result still carrying the document it came from, for the tie-break. */
type DocResult = ScoredResult & { doc: SearchDoc };

const SCORE = {
  titleExact: 100,
  titlePrefix: 80,
  titleStem: 70,
  category: 55,
  ingredientExact: 40,
  ingredientPrefix: 38,
  ingredientStem: 35,
  fuzzyBase: 25,
  /** Whole query appearing verbatim in the title ("bol poke"). */
  phraseBonus: 40,
} as const;

/**
 * Below this many exact-tier results, re-run the query allowing typos.
 *
 * This is the perf contract: a query that already matches something never pays
 * for edit distance across the catalog.
 */
const FUZZY_TRIGGER_THRESHOLD = 8;

/** Prefix matching needs enough characters to be meaningful. */
const MIN_PREFIX_LEN = 3;
/** Words shorter than this are too collision-prone to fuzzy match. */
const MIN_FUZZY_LEN = 4;
/** A corrected word must never outrank one the user actually typed. */
const FUZZY_DISCOUNT = 0.3;

/**
 * Average per-word score below which a match is noise rather than an answer.
 *
 * Derived from the tiers above rather than picked by feel. A typo corrected
 * against a *title* word scores 100 x 0.3 - 1 = 29, or 23 via a prefix; one
 * that only reaches an ingredient stem scores 35 x 0.3 - 1 = 9.5. A floor of 20
 * separates those two: correcting a typo in a dish name is a real match,
 * brushing an ingredient's stem is not.
 *
 * Without it, the non-word "dété" (from mis-tokenizing "plat d'été") returned
 * score-11 hits presented as "1 RECETTE" — a confident answer built on noise.
 * Averaged per matched word so the threshold does not drift with query length.
 */
const MIN_RELEVANCE = 20;

/** Typo budget scales with word length — one edit in `pate`, two in `bourguignon`. */
function maxEditsFor(word: string): number {
  if (word.length < MIN_FUZZY_LEN) return 0;
  return word.length >= 8 ? 2 : 1;
}

function hasPrefix(tokens: string[], word: string): boolean {
  if (word.length < MIN_PREFIX_LEN) return false;
  return tokens.some((t) => t.length > word.length && t.startsWith(word));
}

/**
 * Tie-break for two recipes that matched a typed prefix through different words.
 *
 * #847: « toma » is a prefix of "tomahawk" and of "tomates". Both sit in a
 * title, so both score the title-prefix tier, and the tie went to the source
 * rating: a 5-star steak opened the list above every tomato dish. The word the
 * cook is typing is the completion most of the matching recipes share, so each
 * recipe is weighted by how many of `docs` carry the completion it matched on.
 *
 * Only ever compared after the score, so it reorders ties and nothing else.
 * Counted over the documents being ranked rather than the whole catalog: they
 * are the ones that matched, and it keeps the cost at one pass over them.
 */
function completionWeights(docs: SearchDoc[], words: string[]): Map<SearchDoc, number> {
  const weights = new Map<SearchDoc, number>();
  const prefixes = words.filter((w) => w.length >= MIN_PREFIX_LEN);
  if (prefixes.length === 0 || docs.length < 2) return weights;

  // How many documents carry each completion, per prefix.
  const counts = prefixes.map(() => new Map<string, number>());
  for (const doc of docs) {
    for (const token of doc.fuzzyTokens) {
      for (let i = 0; i < prefixes.length; i++) {
        if (token.length > prefixes[i].length && token.startsWith(prefixes[i])) {
          counts[i].set(token, (counts[i].get(token) ?? 0) + 1);
        }
      }
    }
  }

  const best = (tokens: string[], prefix: string, count: Map<string, number>): number => {
    let max = 0;
    for (const token of tokens) {
      if (token.length > prefix.length && token.startsWith(prefix)) {
        max = Math.max(max, count.get(token) ?? 0);
      }
    }
    return max;
  };

  for (const doc of docs) {
    let weight = 0;
    for (let i = 0; i < prefixes.length; i++) {
      // The title is what scored a title-prefix tie, so its completion decides.
      weight +=
        best(doc.titleTokens, prefixes[i], counts[i]) ||
        best(doc.ingredientTokens, prefixes[i], counts[i]);
    }
    if (weight > 0) weights.set(doc, weight);
  }
  return weights;
}

/** Best exact/prefix/stem score for one query word against one document. */
function scoreWordExact(word: string, stem: string, doc: SearchDoc): number {
  if (doc.titleTokens.includes(word)) return SCORE.titleExact;
  if (hasPrefix(doc.titleTokens, word)) return SCORE.titlePrefix;
  if (doc.titleStems.includes(stem)) return SCORE.titleStem;
  if (doc.categoryTokens.includes(word) || hasPrefix(doc.categoryTokens, word)) {
    return SCORE.category;
  }
  if (doc.ingredientTokens.includes(word)) return SCORE.ingredientExact;
  if (hasPrefix(doc.ingredientTokens, word)) return SCORE.ingredientPrefix;
  if (doc.ingredientStems.includes(stem)) return SCORE.ingredientStem;
  return 0;
}

/**
 * Every distinct token in a document set, cached per array identity.
 *
 * Fuzzy matching per document would re-measure the same words thousands of
 * times — "poulet" appears in hundreds of recipes but is one vocabulary entry.
 * Resolving typos against the vocabulary once turns the hot loop into set
 * lookups. The WeakMap keeps this an implementation detail: callers still just
 * pass `SearchDoc[]`, and the entry is collected with the docs array.
 */
interface Vocabulary {
  tokens: Set<string>;
  /** How many documents each token appears in — see `selectDiscriminatingTerms`. */
  documentFrequency: Map<string, number>;
  /**
   * Resolved typo variants per word. Typing only changes the last word of a
   * query, so on the next keystroke every earlier word is already resolved.
   */
  variants: Map<string, Map<string, number>>;
}

const vocabularyCache = new WeakMap<SearchDoc[], Vocabulary>();

/** Keeps the per-word cache from growing without bound over a long session. */
const MAX_CACHED_VARIANTS = 256;

function vocabularyOf(docs: SearchDoc[]): Vocabulary {
  const cached = vocabularyCache.get(docs);
  if (cached) return cached;
  const tokens = new Set<string>();
  const documentFrequency = new Map<string, number>();
  for (const doc of docs) {
    for (const token of doc.fuzzyTokens) {
      tokens.add(token);
      documentFrequency.set(token, (documentFrequency.get(token) ?? 0) + 1);
    }
  }
  const vocabulary: Vocabulary = { tokens, documentFrequency, variants: new Map() };
  vocabularyCache.set(docs, vocabulary);
  return vocabulary;
}

/** Share of the catalog a term appears in, 0-1. */
export function documentFrequency(docs: SearchDoc[], term: string): number {
  if (docs.length === 0) return 0;
  const { documentFrequency: df } = vocabularyOf(docs);
  const words = tokenize(term);
  if (words.length === 0) return 0;
  // A phrase is at most as common as its rarest word.
  const rarest = Math.min(...words.map((w) => df.get(w) ?? 0));
  return rarest / docs.length;
}

/**
 * Drop terms too common in this catalog to narrow anything.
 *
 * A concept expanded by the model ("asiatique") comes back with a mix of
 * distinctive words (miso, teriyaki) and generic ones (riz, ail). Because
 * include terms are OR'd, a single generic term matches most of the catalog
 * and silently undoes the filter — "asiatique" would return every recipe
 * containing garlic.
 *
 * Measured against the corpus rather than a hand-written stoplist, so it stays
 * correct as the catalog changes and needs no maintenance. If every term is
 * common the original list is returned: a weak filter beats no filter.
 */
export function selectDiscriminatingTerms(
  docs: SearchDoc[],
  terms: string[],
  maxShare = 0.15
): string[] {
  if (terms.length === 0) return terms;
  const kept = terms.filter((term) => documentFrequency(docs, term) <= maxShare);
  return kept.length > 0 ? kept : terms;
}

function variantsFor(word: string, vocabulary: Vocabulary): Map<string, number> {
  const cached = vocabulary.variants.get(word);
  if (cached) return cached;
  const resolved = fuzzyVariants(word, vocabulary.tokens);
  if (vocabulary.variants.size >= MAX_CACHED_VARIANTS) vocabulary.variants.clear();
  vocabulary.variants.set(word, resolved);
  return resolved;
}

/**
 * Tokens within the typo budget of `word`, scored by closeness.
 *
 * The length precheck discards most of the vocabulary for the cost of a
 * subtraction, before the O(n·m) matrix allocation in `levenshteinDistance`.
 */
function fuzzyVariants(word: string, vocabulary: Set<string>): Map<string, number> {
  const variants = new Map<string, number>();
  const maxEdits = maxEditsFor(word);
  if (maxEdits === 0) return variants;

  for (const token of vocabulary) {
    if (Math.abs(token.length - word.length) > maxEdits) continue;
    const distance = levenshteinDistance(word, token);
    if (distance > maxEdits) continue;
    variants.set(token, SCORE.fuzzyBase - distance);
  }
  return variants;
}

/**
 * Best score for one query word against a document, via its typo variants.
 *
 * Scores the *corrected* word through the normal field tiers rather than
 * returning a flat value, so a typo keeps the title-over-ingredients ranking.
 * Without this every fuzzy hit ties and the order collapses to source rating —
 * "poullet" surfaced a bok choy recipe that merely lists chicken stock above
 * every recipe with "Poulet" in its title.
 *
 * The result is discounted so a corrected match never outranks a word the user
 * actually typed, and closer corrections beat looser ones.
 */
function scoreWordFuzzy(variants: Map<string, number>, doc: SearchDoc): number {
  if (variants.size === 0) return 0;
  let best = 0;
  for (const [token, closeness] of variants) {
    const fieldScore = scoreWordExact(token, stemToken(token), doc);
    if (fieldScore === 0) continue;
    const score = fieldScore * FUZZY_DISCOUNT - (SCORE.fuzzyBase - closeness);
    if (score > best) best = score;
  }
  return best;
}

export interface SearchOptions {
  /** Include near-misses even when exact tiers found nothing. Default true. */
  allowFuzzy?: boolean;
  /** Cap on returned results. */
  limit?: number;
  /**
   * Average per-word score required to count as a match. Defaults to
   * `MIN_RELEVANCE`; pass 0 for the "closest matches" fallback, where weak
   * matches are exactly what is wanted.
   */
  minRelevance?: number;
  /**
   * Require every query word to match. Default true.
   *
   * Set false for the "closest matches" fallback: a long query
   * ("asiatique rapide sans porc") has no recipe containing all of its words,
   * so an AND search returns nothing and there is nothing to fall back to.
   * With OR, recipes matching more of the words rank first.
   */
  requireAllWords?: boolean;
}

/**
 * Score and rank recipes for a query.
 *
 * Every query word must match something (AND), and a recipe's score is the sum
 * of each word's best match. Ties break on the source rating, so when relevance
 * genuinely can't separate two recipes the better-reviewed one wins.
 *
 * Returns `[]` for an empty query — callers decide what to show for no input.
 */
export function searchRecipes(
  query: string,
  docs: SearchDoc[],
  options: SearchOptions = {}
): ScoredResult[] {
  const {
    allowFuzzy = true,
    limit,
    requireAllWords = true,
    minRelevance = MIN_RELEVANCE,
  } = options;

  const words = tokenize(query);
  if (words.length === 0) return [];

  const stems = words.map(stemToken);
  const foldedQuery = foldText(query);
  const isPhrase = words.length > 1;

  const rank = (unfiltered: DocResult[]): ScoredResult[] => {
    const results = unfiltered.filter(
      (r) => r.score / Math.max(1, r.matchedWords) >= minRelevance
    );
    const completion = completionWeights(results.map((r) => r.doc), words);
    // Score first, not matched-word count. The sum already rewards matching
    // more of the query (each match adds at least 35), so ordering by count
    // first only let a recipe that brushed two weak words outrank one with a
    // strong title match: "plat d'été" put a Bûche de Noël above "One pot
    // d'été" because the log grazed both "plat" (typo tier) and an "été" in an
    // ingredient note.
    results.sort(
      (a, b) =>
        b.score - a.score ||
        b.matchedWords - a.matchedWords ||
        (completion.get(b.doc) ?? 0) - (completion.get(a.doc) ?? 0) ||
        (b.recipe.sourceRating ?? 0) - (a.recipe.sourceRating ?? 0) ||
        a.recipe.title.localeCompare(b.recipe.title)
    );
    const kept = limit ? results.slice(0, limit) : results;
    return kept.map(({ doc: _doc, ...result }) => result);
  };

  // Tiers 1-5: exact, prefix and stem matching over pre-folded tokens.
  const exact: DocResult[] = [];
  for (const doc of docs) {
    let total = 0;
    let matched = 0;
    for (let i = 0; i < words.length; i++) {
      const wordScore = scoreWordExact(words[i], stems[i], doc);
      if (wordScore === 0) {
        if (requireAllWords) { matched = -1; break; }
        continue;
      }
      matched++;
      total += wordScore;
    }
    if (matched <= 0) continue;
    if (isPhrase && doc.foldedTitle.includes(foldedQuery)) total += SCORE.phraseBonus;
    exact.push({ doc, recipe: doc.recipe, score: total, matchedWords: matched, fuzzyOnly: false });
  }

  if (!allowFuzzy || exact.length >= FUZZY_TRIGGER_THRESHOLD) {
    return rank(exact);
  }

  // Tier 6: typo tolerance. Resolve each word's variants against the shared
  // vocabulary once, then match documents with set lookups.
  const vocabulary = vocabularyOf(docs);
  const variantsPerWord = words.map((word) => variantsFor(word, vocabulary));

  // Words that matched exactly keep their exact score, so a one-typo-in-three
  // query still outranks an all-fuzzy match.
  const seen = new Set(exact.map((r) => r.recipe.id));
  const fuzzy: DocResult[] = [];
  for (const doc of docs) {
    if (seen.has(doc.recipe.id)) continue;
    let total = 0;
    let matched = 0;
    let usedFuzzy = false;
    for (let i = 0; i < words.length; i++) {
      let wordScore = scoreWordExact(words[i], stems[i], doc);
      if (wordScore === 0) {
        wordScore = scoreWordFuzzy(variantsPerWord[i], doc);
        usedFuzzy = true;
      }
      if (wordScore === 0) {
        if (requireAllWords) { matched = -1; break; }
        continue;
      }
      matched++;
      total += wordScore;
    }
    if (matched <= 0) continue;
    fuzzy.push({ doc, recipe: doc.recipe, score: total, matchedWords: matched, fuzzyOnly: usedFuzzy });
  }

  return rank([...exact, ...fuzzy]);
}

/** Convenience wrapper for callers that only want the recipes. */
export function searchRecipeEntries(
  query: string,
  docs: SearchDoc[],
  options?: SearchOptions
): PersonalRecipeIndexEntry[] {
  return searchRecipes(query, docs, options).map((r) => r.recipe);
}

/**
 * Weight applied to a term the model inferred rather than one the user typed.
 *
 * Same value and same reasoning as `FUZZY_DISCOUNT`: a word the app supplied on
 * the user's behalf must never outrank a word the user actually wrote. At 0.3 a
 * *title* hit on an inferred term scores 30, which lands below the weakest tier
 * a typed word can reach (an ingredient stem, 35). So "poulet asiatique" ranks a
 * recipe containing chicken above one merely titled "Ramen".
 */
export const AI_TERM_DISCOUNT = 0.3;

/**
 * Terms to rank a filtered result list against, split by who supplied them.
 *
 * Each entry is an OR-group scored on its best term, because a group holds
 * alternative spellings of one idea — the synonyms of a word the user typed, or
 * the model's expansion of one concept. Summing within a group would let a
 * recipe brushing several generic expansions ("riz", "sésame") outrank one named
 * after the concept itself.
 *
 * Groups are then summed, so answering more of the query scores higher.
 */
export interface RankingTerms {
  /** One group per word the user typed, each holding that word's synonyms. */
  user: string[][];
  /** One group per filter the model inferred. Scored at `AI_TERM_DISCOUNT`. */
  ai: string[][];
}

/** A term with its words resolved once, rather than per document. */
interface PreparedTerm {
  words: string[];
  stems: string[];
}

function prepareGroups(groups: string[][]): PreparedTerm[][] {
  return groups
    .map((group) =>
      group
        .map((term) => {
          const words = tokenize(term);
          return { words, stems: words.map(stemToken) };
        })
        .filter((term) => term.words.length > 0)
    )
    .filter((group) => group.length > 0);
}

/**
 * Every word of a multi-word term must match — "purée d'ail" is not satisfied by
 * garlic alone, or every recipe containing garlic ranks as though it were the
 * dish. Mirrors `docMatchesTerm`, which gates the filter the same way.
 */
function scoreTerm(doc: SearchDoc, term: PreparedTerm): number {
  let total = 0;
  for (let i = 0; i < term.words.length; i++) {
    const score = scoreWordExact(term.words[i], term.stems[i], doc);
    if (score === 0) return 0;
    total += score;
  }
  return total;
}

function scoreGroups(doc: SearchDoc, groups: PreparedTerm[][]): number {
  let total = 0;
  for (const group of groups) {
    let best = 0;
    for (const term of group) {
      const score = scoreTerm(doc, term);
      if (score > best) best = score;
    }
    total += best;
  }
  return total;
}

/**
 * Order an already-filtered result list by relevance.
 *
 * The filter decides which recipes come back; this decides only the order, so
 * it returns every document it was given. Recipes nothing matched score 0 and
 * sink to the end, where the best-reviewed goes first — the honest fallback when
 * relevance has nothing to say, and still far better than catalog order.
 *
 * No typo tier: the filter that produced these documents matched exactly (see
 * `docMatchesTerm`), so fuzzy scoring here would rank on a signal that could not
 * have admitted the recipe in the first place.
 *
 * Fixes #578, where survivors kept catalog order and two unrelated queries
 * therefore opened on the same recipe.
 */
export function rankByTerms(
  docs: SearchDoc[],
  terms: RankingTerms
): PersonalRecipeIndexEntry[] {
  const user = prepareGroups(terms.user);
  const ai = prepareGroups(terms.ai);

  const scored = docs.map((doc) => ({
    doc,
    recipe: doc.recipe,
    score: scoreGroups(doc, user) + AI_TERM_DISCOUNT * scoreGroups(doc, ai),
  }));
  // The words the cook typed, for the prefix tie-break (#847).
  const typed = Array.from(new Set(user.flatMap((group) => group.flatMap((term) => term.words))));
  const completion = completionWeights(docs, typed);

  scored.sort(
    (a, b) =>
      b.score - a.score ||
      (completion.get(b.doc) ?? 0) - (completion.get(a.doc) ?? 0) ||
      (b.recipe.sourceRating ?? 0) - (a.recipe.sourceRating ?? 0) ||
      a.recipe.title.localeCompare(b.recipe.title)
  );
  return scored.map((s) => s.recipe);
}

/**
 * Does a single recipe match a term? Used by the filter-token predicates, which
 * test one recipe at a time rather than ranking the catalog.
 *
 * Exact/prefix/stem only — no typo tolerance. A negation token ("sans porc")
 * that fuzzy-matched would exclude recipes for words the user never typed.
 */
export function docMatchesTerm(doc: SearchDoc, term: string): boolean {
  const prepared = prepareTerm(term);
  return prepared !== null && docMatchesPreparedTerm(doc, prepared);
}

/** A term tokenized and stemmed once, so it can be tested against many docs. */
export interface PreparedPantryTerm {
  words: string[];
  stems: string[];
}

function prepareTerm(term: string): PreparedPantryTerm | null {
  const words = tokenize(term);
  if (words.length === 0) return null;
  return { words, stems: words.map(stemToken) };
}

/**
 * Prepare a list of terms (e.g. every active pantry name) once. Terms that
 * tokenize to nothing are dropped, exactly as `docMatchesTerm` rejects them.
 *
 * #668: the "Mon frigo" chip re-tokenized all 232 pantry names for each of
 * 1615 recipes on every tap. Preparing them once keeps the same matching rules.
 */
export function prepareTerms(terms: string[]): PreparedPantryTerm[] {
  const prepared: PreparedPantryTerm[] = [];
  for (const term of terms) {
    const p = prepareTerm(term);
    if (p) prepared.push(p);
  }
  return prepared;
}

function docMatchesPreparedTerm(doc: SearchDoc, term: PreparedPantryTerm): boolean {
  for (let i = 0; i < term.words.length; i++) {
    if (scoreWordExact(term.words[i], term.stems[i], doc) <= 0) return false;
  }
  return true;
}

/** Does the recipe match at least one of the prepared terms? */
export function docMatchesAnyPreparedTerm(doc: SearchDoc, terms: PreparedPantryTerm[]): boolean {
  for (const term of terms) {
    if (docMatchesPreparedTerm(doc, term)) return true;
  }
  return false;
}
